import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import type { PreviewStartOptions } from '@peritasai/types';
import type { CameraSnapshotEntity } from './camera-snapshot.entity';
import { cameraRepository } from './repository';

export const cameraKeys = {
  all: ['camera'] as const,
  preview: () => [...cameraKeys.all, 'preview'] as const,
  snapshot: () => [...cameraKeys.all, 'snapshot'] as const,
} as const;

/**
 * Start a camera preview. Imperative command over device state (not cacheable),
 * so modeled as a mutation; resolves with the MJPEG stream URL.
 */
export function useStartPreview(): UseMutationResult<
  string,
  Error,
  PreviewStartOptions | undefined
> {
  return useMutation({
    mutationKey: [...cameraKeys.preview(), 'start'],
    mutationFn: (options?: PreviewStartOptions) =>
      cameraRepository.startPreview(options),
  });
}

/** Stop the active camera preview. */
export function useStopPreview(): UseMutationResult<void, Error, void> {
  return useMutation({
    mutationKey: [...cameraKeys.preview(), 'stop'],
    mutationFn: () => cameraRepository.stopPreview(),
  });
}

/** Capture the current camera frame. */
export function useTakeSnapshot(): UseMutationResult<
  CameraSnapshotEntity,
  Error,
  string | undefined
> {
  return useMutation({
    mutationKey: cameraKeys.snapshot(),
    mutationFn: (trayAssemblyId?: string) =>
      cameraRepository.takeSnapshot(trayAssemblyId),
  });
}
